/**
 * ReviewMoneyCard — what we recorded against what Stripe actually did
 * (BON-381).
 *
 * Three states, and the page renders fully in all of them:
 *   - a Stripe snapshot: authorized / captured / refunded, and the
 *     unreconciled remainder against our own order total;
 *   - no PaymentIntent at all: say so, rather than showing zeroes that read
 *     like a reconciled $0;
 *   - `stripeUnavailable`: an honest "couldn't reach Stripe". The queue is the
 *     mitigation for payments having gone wrong, so a Stripe outage must not be
 *     able to take this page down with it. Our own recorded figures still show.
 */
import Explanation from './components/ReviewMoneyExplanation';
import Row from './components/ReviewMoneyRow';
import StripeRows from './components/ReviewMoneyStripeRows';
import StripeDown from './components/ReviewStripeDown';
import { formatMoney, orderCountLabel } from './reviewQueueModel';
import type { ReviewDetail } from './reviewTypes';

export interface ReviewMoneyCardProps {
  detail: ReviewDetail;
}

export function ReviewMoneyCard({ detail }: ReviewMoneyCardProps) {
  const { stripe, currency } = detail;

  return (
    <section className="nb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <h3
        className="nb-display"
        style={{ margin: '0 0 2px', fontSize: 15, color: 'var(--nb-bark-dark)' }}
      >
        Money
      </h3>
      <Row
        label={`Order · ${orderCountLabel(detail)}`}
        value={formatMoney(detail.totalCents, currency)}
      />
      {detail.serviceFeeCents > 0 && (
        <Row label="Service fee" value={formatMoney(detail.serviceFeeCents, currency)} />
      )}
      {detail.amountPaidCents !== null && (
        <Row label="Recorded paid" value={formatMoney(detail.amountPaidCents, currency)} />
      )}
      {stripe && <StripeRows detail={detail} stripe={stripe} currency={currency} />}
      <Explanation detail={detail} />
      {detail.stripeUnavailable && <StripeDown paymentIntentId={detail.paymentIntentId} />}
      {!stripe && !detail.stripeUnavailable && (
        <div style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--nb-subtle)' }}>
          No payment intent was ever created, so nothing was authorized or captured.
        </div>
      )}
    </section>
  );
}
